import { LitElement, html } from 'lit';
import styles from './studio-card.css.mjs';

export class StudioCard extends LitElement {
  static styles = styles;

  static properties = {
    variant: { type: String, reflect: true },
    state: { type: String, reflect: true },
    cardType: { type: String, reflect: true },
  };

  static validVariants = ['vertical', 'horizontal'];
  static validStates = ['active', 'pending', 'expired', ''];
  static validCardTypes = ['webStoryCard', 'damCard', 'keywordCard', ''];

  constructor() {
    super();
    this.variant = 'vertical';
    this.state = '';
    this.cardType = '';
  }

  set variant(value) {
    this._variant = StudioCard.validVariants.includes(value) ? value : 'vertical';
    this.requestUpdate('variant');
  }

  get variant() {
    return this._variant;
  }

  set state(value) {
    this._state = StudioCard.validStates.includes(value) ? value : '';
    this.requestUpdate('state');
  }

  get state() {
    return this._state;
  }

  set cardType(value) {
    this._cardType = StudioCard.validCardTypes.includes(value) ? value : '';
    this.requestUpdate('cardType');
  }

  get cardType() {
    return this._cardType;
  }

  isVisible = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  #onBodyChange = (e) => {
    // get slotted elements in body
    const assigned = [...e.target.assignedElements({ flatten: true })];

    // check visibility
    const hasVisibleSelector = assigned.some(el =>
      (el.part?.contains('selector') || el.matches('[part~="selector"]')) &&
      this.isVisible(el)
    );

    this.toggleAttribute('has-selector', hasVisibleSelector);

    // set up observers on relevant elements so we react to runtime visibility changes
    assigned.forEach(el => {
      if (el.part?.contains('selector') || el.matches('[part~="selector"]')) {
        const observer = new MutationObserver(() => {
          const stillVisible = this.isVisible(el);
          this.toggleAttribute('has-selector', stillVisible);
        });
        observer.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
      }
    });
  };

  render() {
    const stateClass = this.state ? `state-${this.state}` : '';
    const cardTypeClass = this.cardType ? `type-${this.cardType}` : '';
    const ariaLabel = this.cardType ? `${this.cardType} Card` : 'Content Card';

    return html`
      <div
        class="card ${this.variant} ${stateClass} ${cardTypeClass}"
        role="region"
        aria-label="${ariaLabel}"
      >
        <div class="media">
          <slot name="media"></slot>
        </div>
        <div class="content-wrapper">
          <div class="body">
            <slot name="body" @slotchange=${this.#onBodyChange}></slot>
          </div>
          <div class="footer">
            <slot name="footer"></slot>
          </div>
        </div>
        <div class="overlay">
          <div class="actions-container">
            <slot name="actions"></slot>
          </div>
        </div>
        <slot></slot>
      </div>
    `;
  }
}

customElements.define('studio-card', StudioCard);